import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Input, Badge } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  canManagePmEvents,
  buildPreviewMergeValues,
  applyPmEmailPreviewMerges,
  PM_PAYMENT_TIER_LABELS,
} from '../../lib/pmEventUtils';
import { formatSessionOptionLabel } from '../../lib/pmEventTime';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import {
  loadUsersDirectory,
  resolveDisplayName,
} from '../../lib/usersDirectory';
import { todayDateKey } from '../../lib/kpiTrackerUtils';
import * as FiIcons from 'react-icons/fi';

const {
  FiArrowLeft,
  FiPlus,
  FiCheck,
  FiMail,
  FiSend,
  FiChevronDown,
  FiChevronUp,
  FiColumns,
  FiUserPlus,
} = FiIcons;

/** Fallback when the event has no saved reminder template to seed the custom email from. */
const DEFAULT_CUSTOM_EMAIL_SUBJECT =
  'Reminder: {{event_title}} is coming up on {{session_date}}';

const DEFAULT_CUSTOM_EMAIL_BODY = `<p>Hi {{first_name}},</p>
<p>This is a quick reminder that <strong>{{event_title}}</strong> is coming up.</p>
<ul>
  <li><strong>Date:</strong> {{session_date}}</li>
  <li><strong>Time:</strong> {{session_time}}</li>
  <li><strong>Where:</strong> {{session_location}}</li>
</ul>
<p>We're looking forward to seeing you there.</p>
<p>— The Prosperity Leaders Team</p>`;

const ADMIN_ROLES = new Set(['admin', 'super_admin']);
/** Column visible to Manager + Admin + Super Admin */
const LEAD_OWNER_COLUMN_ROLES = new Set(['manager', 'admin', 'super_admin']);

const LIST_VIEW_KEY = 'pm_event_registrations';

/** Toggleable columns (Name is pinned and always visible). */
const TOGGLEABLE_COLUMNS = [
  { key: 'contact', label: 'Contact' },
  { key: 'source', label: 'Source' },
  { key: 'invite', label: 'Invite' },
  { key: 'referred_by', label: 'Referred by' },
  { key: 'lead_owner', label: 'Lead Owner', requiresLeadOwner: true },
  { key: 'registered', label: 'Registered' },
  { key: 'confirmation_status', label: 'Confirmation Status' },
];

const TOGGLEABLE_COLUMN_KEYS = new Set(TOGGLEABLE_COLUMNS.map((c) => c.key));

const NAME_COL_CLASS =
  'sticky left-0 z-[1] min-w-[14rem] max-w-[18rem] border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]';
const NAME_COL_CLASS_WITH_CHECK =
  'sticky left-10 z-[1] min-w-[14rem] max-w-[18rem] border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]';
const CHECK_COL_CLASS =
  'sticky left-0 z-[2] w-10 min-w-[2.5rem] px-2 py-3 bg-gray-50 border-r border-gray-200';
const CHECK_CELL_CLASS =
  'sticky left-0 z-[2] w-10 min-w-[2.5rem] px-2 py-3 bg-white group-hover:bg-gray-50 border-r border-gray-200';
const H_SCROLLBAR_HIDE =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

/** Same upcoming-session idea as list-pm-events / EventList tabs. */
function eventHasUpcomingSession(sessions, nowMs = Date.now()) {
  return (Array.isArray(sessions) ? sessions : []).some((s) => {
    if (!s?.starts_at) return false;
    const t = new Date(s.starts_at).getTime();
    return Number.isFinite(t) && t >= nowMs;
  });
}

function soonestUpcomingMs(sessions, nowMs = Date.now()) {
  const times = (Array.isArray(sessions) ? sessions : [])
    .map((s) => {
      if (!s?.starts_at) return null;
      const t = new Date(s.starts_at).getTime();
      return Number.isFinite(t) && t >= nowMs ? t : null;
    })
    .filter((t) => t != null)
    .sort((a, b) => a - b);
  return times[0] ?? Number.POSITIVE_INFINITY;
}

function soonestUpcomingSession(sessions, nowMs = Date.now()) {
  let best = null;
  let bestMs = Number.POSITIVE_INFINITY;
  for (const s of Array.isArray(sessions) ? sessions : []) {
    const t = s?.starts_at ? new Date(s.starts_at).getTime() : NaN;
    if (Number.isFinite(t) && t >= nowMs && t < bestMs) {
      best = s;
      bestMs = t;
    }
  }
  return best;
}

const DEFAULT_FILTERS = {
  search: '',
  checkIn: 'all',
  source: 'all',
  session: 'all',
  associate: 'all',
  rsvpStatus: 'all',
  regType: 'all',
  confirmationStatus: 'all',
  reminderStatus: 'all',
  leadLinked: 'all',
  invitedVia: 'all',
  inviteSlug: 'all',
  referredBy: 'all',
  leadOwner: 'all',
};

const EMAIL_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'not_sent', label: 'Not sent' },
  { value: 'sent', label: 'Sent' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'failed', label: 'Failed' },
];

/** Manual CRM outreach/RSVP outcome (pm_event_registrations.confirmation_status). */
const RSVP_STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'not_sure', label: 'Not Sure' },
];

const RSVP_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...RSVP_STATUS_OPTIONS,
  { value: 'unset', label: 'Unset' },
];

const RSVP_STATUS_STYLE = {
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  no_answer: 'bg-gray-100 text-gray-600 border-gray-200',
  not_sure: 'bg-gray-100 text-gray-600 border-gray-200',
};

function rsvpStatusLabel(status) {
  if (!status) return '—';
  return RSVP_STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
}

function rsvpStatusClassName(status) {
  return RSVP_STATUS_STYLE[status] || 'bg-gray-100 text-gray-600 border-gray-200';
}

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? format(d, 'MMM d, yyyy h:mm a') : '—';
}

function inviteOptionLabel(invite) {
  const slug = invite?.slug ? `/${invite.slug}` : 'invite';
  if (invite?.user?.full_name || invite?.user?.email) {
    return `${invite.user.full_name || invite.user.email} (${slug})`;
  }
  if (invite?.mentors_user_id) {
    return `Student invite (${slug})`;
  }
  return slug;
}

const EMAIL_STATUS_BADGE = {
  not_sent: { label: 'Not Sent', variant: 'default', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Sent', variant: 'default', className: 'bg-blue-100 text-blue-800' },
  opened: { label: 'Opened', variant: 'warning' },
  clicked: { label: 'Clicked', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

function EmailStatusBadge({ status }) {
  const cfg = EMAIL_STATUS_BADGE[status] || EMAIL_STATUS_BADGE.not_sent;
  return (
    <Badge variant={cfg.variant} className={cfg.className}>
      {cfg.label}
    </Badge>
  );
}

const PAYMENT_STATUS_BADGE = {
  pending: { label: 'Pending', variant: 'default', className: 'bg-gray-100 text-gray-600' },
  paid: { label: 'Paid', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  refunded: { label: 'Refunded', variant: 'default', className: 'bg-gray-100 text-gray-600' },
};

/** Blank for 'not_required' — covers registrations taken before payment was turned on. */
function PaymentStatusBadge({ status, tier }) {
  const cfg = PAYMENT_STATUS_BADGE[status];
  if (!cfg) return null;
  const tierLabel = status === 'paid' ? PM_PAYMENT_TIER_LABELS[tier] : null;
  return (
    <div>
      <Badge variant={cfg.variant} className={cfg.className}>
        {cfg.label}
      </Badge>
      {tierLabel && <div className="text-xs text-gray-500 mt-0.5">{tierLabel}</div>}
    </div>
  );
}

function registrantDisplayName(reg) {
  return [reg?.first_name, reg?.last_name].filter(Boolean).join(' ') || 'Unnamed';
}

function isAssociateReg(reg) {
  return Boolean(reg?.matched_user_id) || reg?.manually_flagged_associate === true;
}

function sessionOptionLabel(session) {
  return session?.title?.trim() || formatSessionOptionLabel(session);
}

function leadOwnerLabel(lead) {
  if (!lead) return null;
  if (!lead.created_by) return 'Unassigned';
  return resolveDisplayName(lead.created_by, lead.owner) || 'Unassigned';
}

/** Admin / Super Admin only: instant reassign lead.created_by (and exclusive_to when exclusive). */
function ChangeLeadOwnerModal({ isOpen, onClose, reg, onSaved, saving }) {
  const [newOwnerId, setNewOwnerId] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setNewOwnerId(null);
      return;
    }
    setNewOwnerId(reg?.lead?.created_by ?? null);
  }, [isOpen, reg]);

  const currentLabel = leadOwnerLabel(reg?.lead) || 'Unassigned';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title="Change Lead Owner"
      className="max-w-md"
    >
      <p className="text-sm text-gray-500 mb-1">
        {reg ? registrantDisplayName(reg) : 'Registration'}
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Current owner: {currentLabel}
      </p>
      <UserPickerDropdown
        label="New owner"
        source="directory"
        value={newOwnerId}
        onChange={setNewOwnerId}
        placeholder="Select owner…"
        disabled={saving}
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          isLoading={saving}
          disabled={
            saving
            || !newOwnerId
            || !reg?.lead_id
            || newOwnerId === (reg?.lead?.created_by ?? null)
          }
          onClick={() => onSaved(newOwnerId)}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}

/** Manager only: submit ownership transfer request for Admin/Super Admin approval. */
function RequestLeadOwnerChangeModal({ isOpen, onClose, reg, onSubmit, saving }) {
  const [proposedOwnerId, setProposedOwnerId] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setProposedOwnerId(null);
      setNotes('');
      return;
    }
    setProposedOwnerId(null);
    setNotes('');
  }, [isOpen, reg]);

  const currentLabel = leadOwnerLabel(reg?.lead) || 'Unassigned';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title="Request Owner Change"
      className="max-w-md"
    >
      <p className="text-sm text-gray-500 mb-1">
        {reg ? registrantDisplayName(reg) : 'Registration'}
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Current owner: {currentLabel}. An Admin or Super Admin must approve this request.
      </p>
      <UserPickerDropdown
        label="Proposed new owner"
        source="directory"
        value={proposedOwnerId}
        onChange={setProposedOwnerId}
        placeholder="Select proposed owner…"
        disabled={saving}
      />
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Why should ownership change?"
          disabled={saving}
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          isLoading={saving}
          disabled={
            saving
            || !proposedOwnerId
            || !reg?.lead_id
            || !reg?.lead?.created_by
            || proposedOwnerId === (reg?.lead?.created_by ?? null)
          }
          onClick={() => onSubmit({ proposedOwnerId, notes })}
        >
          Submit request
        </Button>
      </div>
    </Modal>
  );
}

function FilterSelect({ label, value, onChange, options, className = '' }) {
  return (
    <label className={`flex flex-col gap-0.5 min-w-[8.5rem] ${className}`}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <select
        className="h-8 px-2 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Searchable lead picker — same debounce + api.applications.searchLeads pattern as ApplicationDetail. */
function LinkLeadModal({ isOpen, onClose, agencyId, onSelect, linking }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !agencyId) return;
    if (!query || query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await api.applications.searchLeads(query, agencyId);
        if (!cancelled) setResults(data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [query, agencyId, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={() => !linking && onClose()} title="Link to Lead" className="max-w-md">
      <p className="text-sm text-gray-500 mb-4">
        Search agency leads by name, email, or phone, then select one to link.
      </p>
      <Input
        placeholder="Search by name, phone, or email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        disabled={linking}
      />
      <div className="mt-3 min-h-[8rem]">
        {loading && (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-gray-500">
            <Spinner className="text-primary" />
            Searching…
          </div>
        )}
        {!loading && query.length >= 2 && results.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">No leads found</p>
        )}
        {!loading && results.length > 0 && (
          <ul className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {results.map((lead) => {
              const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unnamed lead';
              return (
                <li key={lead.id}>
                  <button
                    type="button"
                    disabled={linking}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => onSelect(lead)}
                  >
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {[lead.phone, lead.email].filter(Boolean).join(' · ') || lead.lead_type || '—'}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && query.length < 2 && (
          <p className="text-xs text-gray-400 text-center py-8">Type at least 2 characters to search</p>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="outline" disabled={linking} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

export default function EventRegistrations() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const canManage = canManagePmEvents(user);
  const canSeeLeadOwner = LEAD_OWNER_COLUMN_ROLES.has(user?.role);
  const canInstantReassign = ADMIN_ROLES.has(user?.role);
  const canRequestOwnerChange = user?.role === 'manager';

  const [event, setEvent] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Hidden entirely for free events rather than shown as a column of blanks.
  const showPaymentColumn = Boolean(event?.requires_payment);
  const [doorOpen, setDoorOpen] = useState(false);
  const [doorForm, setDoorForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    session_id: '',
    invitation_link_id: '',
    invite_search: '',
    check_in_now: true,
  });
  const [saving, setSaving] = useState(false);
  const [reminderConfirmOpen, setReminderConfirmOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [customEmailOpen, setCustomEmailOpen] = useState(false);
  const [customEmailForm, setCustomEmailForm] = useState({ subject: '', html_body: '' });
  const [customEmailPreviewing, setCustomEmailPreviewing] = useState(false);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const customEmailPrefilled = useRef(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [reinviteOpen, setReinviteOpen] = useState(false);
  const [reinviteTargets, setReinviteTargets] = useState([]);
  const [reinviteLoading, setReinviteLoading] = useState(false);
  const [reinviteTargetId, setReinviteTargetId] = useState('');
  const [sendingReinvite, setSendingReinvite] = useState(false);
  const [linkReg, setLinkReg] = useState(null);
  const [linking, setLinking] = useState(false);
  const [creatingLeadId, setCreatingLeadId] = useState(null);
  const [ownerReg, setOwnerReg] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [requestReg, setRequestReg] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [scrollContentWidth, setScrollContentWidth] = useState(0);

  const topScrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const bodyTableRef = useRef(null);
  const columnsMenuRef = useRef(null);
  const scrollSyncLock = useRef(false);

  const isColVisible = useCallback(
    (key) => !hiddenColumns.includes(key),
    [hiddenColumns],
  );

  const toggleableColumns = useMemo(
    () =>
      TOGGLEABLE_COLUMNS.filter(
        (col) => !col.requiresLeadOwner || canSeeLeadOwner,
      ),
    [canSeeLeadOwner],
  );

  useEffect(() => {
    if (canSeeLeadOwner) void loadUsersDirectory();
  }, [canSeeLeadOwner]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pref = await api.userListViewPreferences.get(LIST_VIEW_KEY);
        if (cancelled) return;
        const cols = Array.isArray(pref?.hidden_columns)
          ? pref.hidden_columns.filter((k) => TOGGLEABLE_COLUMN_KEYS.has(k))
          : [];
        setHiddenColumns(cols);
      } catch (err) {
        console.error('[EventRegistrations] load column prefs failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!columnsMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) {
        setColumnsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [columnsMenuOpen]);

  const syncScrollLeft = useCallback((source, left) => {
    if (scrollSyncLock.current) return;
    scrollSyncLock.current = true;
    const targets = [topScrollRef, headerScrollRef, bodyScrollRef];
    for (const ref of targets) {
      if (ref.current && ref !== source) {
        ref.current.scrollLeft = left;
      }
    }
    requestAnimationFrame(() => {
      scrollSyncLock.current = false;
    });
  }, []);

  const measureScrollWidth = useCallback(() => {
    const bodyTable = bodyTableRef.current;
    const headerTable = headerScrollRef.current?.querySelector('table');
    const width = bodyTable?.scrollWidth || bodyScrollRef.current?.scrollWidth || 0;
    setScrollContentWidth((prev) => (prev === width ? prev : width));

    if (!bodyTable || !headerTable) return;
    const firstRow = bodyTable.querySelector('tbody tr');
    if (!firstRow) return;
    const bodyCells = firstRow.children;
    const headerCells = headerTable.querySelectorAll('thead th');
    if (bodyCells.length !== headerCells.length) return;
    for (let i = 0; i < bodyCells.length; i += 1) {
      const w = Math.ceil(bodyCells[i].getBoundingClientRect().width);
      if (w <= 0) continue;
      const px = `${w}px`;
      headerCells[i].style.width = px;
      headerCells[i].style.minWidth = px;
      headerCells[i].style.maxWidth = px;
    }
    if (width > 0) {
      headerTable.style.width = `${width}px`;
      bodyTable.style.width = `${width}px`;
    }
  }, []);

  const handleToggleColumn = useCallback(
    (key) => {
      setHiddenColumns((prev) => {
        const next = prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];
        void api.userListViewPreferences.save(LIST_VIEW_KEY, next).catch((err) => {
          console.error('[EventRegistrations] save column prefs failed:', err);
          addToast({
            type: 'error',
            title: 'Could not save column preferences',
            message: err.message,
          });
        });
        return next;
      });
    },
    [addToast],
  );

  const registrantsWithEmail = useMemo(
    () => regs.filter((reg) => String(reg.email || '').trim()),
    [regs],
  );

  const inviteOptions = useMemo(() => {
    const links = event?.invitation_links ?? [];
    const q = String(doorForm.invite_search || '').trim().toLowerCase();
    if (!q) return links;
    return links.filter((inv) => {
      const label = inviteOptionLabel(inv).toLowerCase();
      const slug = String(inv.slug || '').toLowerCase();
      return label.includes(q) || slug.includes(q);
    });
  }, [event?.invitation_links, doorForm.invite_search]);

  const sessionFilterOptions = useMemo(() => {
    const sessions = event?.sessions ?? [];
    return [
      { value: 'all', label: 'All' },
      ...sessions.map((s) => ({ value: s.id, label: sessionOptionLabel(s) })),
    ];
  }, [event?.sessions]);

  const inviteSlugFilterOptions = useMemo(() => {
    const links = event?.invitation_links ?? [];
    return [
      { value: 'all', label: 'All' },
      ...links.map((inv) => ({
        value: inv.id,
        label: inv.slug ? `/${inv.slug}` : inviteOptionLabel(inv),
      })),
    ];
  }, [event?.invitation_links]);

  const referredByFilterOptions = useMemo(() => {
    const values = [];
    const seen = new Set();
    for (const reg of regs) {
      const text = reg?.referred_by_text;
      if (!text || seen.has(text)) continue;
      seen.add(text);
      values.push(text);
    }
    values.sort((a, b) => a.localeCompare(b));
    return [
      { value: 'all', label: 'All' },
      ...values.map((v) => ({ value: v, label: v })),
    ];
  }, [regs]);

  const leadOwnerFilterOptions = useMemo(() => {
    if (!canSeeLeadOwner) return [{ value: 'all', label: 'All' }];
    const names = [];
    const seen = new Set();
    for (const reg of regs) {
      if (!reg.lead_id) continue;
      const label = leadOwnerLabel(reg.lead) || 'Unassigned';
      if (label === 'Unassigned') continue;
      if (seen.has(label)) continue;
      seen.add(label);
      names.push(label);
    }
    names.sort((a, b) => a.localeCompare(b));
    return [
      { value: 'all', label: 'All' },
      { value: '__unassigned__', label: 'Unassigned' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [regs, canSeeLeadOwner]);

  const moreFiltersActiveCount = useMemo(() => {
    let count = 0;
    if (filters.regType !== 'all') count += 1;
    if (filters.confirmationStatus !== 'all') count += 1;
    if (filters.reminderStatus !== 'all') count += 1;
    if (filters.leadLinked !== 'all') count += 1;
    if (filters.invitedVia !== 'all') count += 1;
    if (filters.inviteSlug !== 'all') count += 1;
    if (filters.referredBy !== 'all') count += 1;
    if (canSeeLeadOwner && filters.leadOwner !== 'all') count += 1;
    return count;
  }, [filters, canSeeLeadOwner]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search.trim() !== ''
      || filters.checkIn !== 'all'
      || filters.source !== 'all'
      || filters.session !== 'all'
      || filters.associate !== 'all'
      || filters.rsvpStatus !== 'all'
      || moreFiltersActiveCount > 0
    );
  }, [filters, moreFiltersActiveCount]);

  const filteredRegs = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return regs.filter((reg) => {
      if (q) {
        const hay = [reg.first_name, reg.last_name, reg.email, reg.phone]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        if (!hay.includes(q)) return false;
      }
      if (filters.checkIn === 'checked_in' && !reg.checked_in_at) return false;
      if (filters.checkIn === 'not_checked_in' && reg.checked_in_at) return false;
      if (filters.source === 'self' && reg.source !== 'self') return false;
      if (filters.source === 'door' && reg.source !== 'door') return false;
      if (filters.session !== 'all' && reg.session_id !== filters.session) return false;
      const associate = isAssociateReg(reg);
      if (filters.associate === 'associate' && !associate) return false;
      if (filters.associate === 'non_associate' && associate) return false;
      if (filters.rsvpStatus === 'unset') {
        if (reg.confirmation_status != null) return false;
      } else if (filters.rsvpStatus !== 'all' && reg.confirmation_status !== filters.rsvpStatus) {
        return false;
      }
      if (filters.regType === 'primary' && reg.is_guest_of) return false;
      if (filters.regType === 'guest' && !reg.is_guest_of) return false;
      if (filters.confirmationStatus !== 'all' && reg.confirmation_email_status !== filters.confirmationStatus) {
        return false;
      }
      if (filters.reminderStatus !== 'all' && reg.reminder_email_status !== filters.reminderStatus) {
        return false;
      }
      if (filters.leadLinked === 'has_lead' && !reg.lead_id) return false;
      if (filters.leadLinked === 'no_lead' && reg.lead_id) return false;
      if (filters.invitedVia === 'has_invite' && !reg.invitation_link_id) return false;
      if (filters.invitedVia === 'direct_or_door' && reg.invitation_link_id) return false;
      if (filters.inviteSlug !== 'all' && reg.invitation_link_id !== filters.inviteSlug) return false;
      if (filters.referredBy !== 'all' && reg.referred_by_text !== filters.referredBy) return false;
      if (canSeeLeadOwner && filters.leadOwner !== 'all') {
        // Column shows "—" when no linked lead; "Unassigned" only for linked lead with no owner.
        if (!reg.lead_id) return false;
        const ownerLabel = leadOwnerLabel(reg.lead) || 'Unassigned';
        if (filters.leadOwner === '__unassigned__') {
          if (ownerLabel !== 'Unassigned') return false;
        } else if (ownerLabel !== filters.leadOwner) {
          return false;
        }
      }
      return true;
    });
  }, [regs, filters, canSeeLeadOwner]);

  const pageIds = useMemo(() => filteredRegs.map((r) => r.id), [filteredRegs]);
  const allPageSelected =
    canManage && pageIds.length > 0 && pageIds.every((rid) => selectedIds.has(rid));
  const somePageSelected =
    canManage && pageIds.some((rid) => selectedIds.has(rid)) && !allPageSelected;
  const selectedCount = selectedIds.size;
  const nameColClass = canManage ? NAME_COL_CLASS_WITH_CHECK : NAME_COL_CLASS;

  const toggleSelectOne = useCallback((regId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(regId)) next.delete(regId);
      else next.add(regId);
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = pageIds.length > 0 && pageIds.every((rid) => next.has(rid));
      if (allSelected) {
        for (const rid of pageIds) next.delete(rid);
      } else {
        for (const rid of pageIds) next.add(rid);
      }
      return next;
    });
  }, [pageIds]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [id]);

  // Drop selections that are no longer in the loaded list
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const valid = new Set(regs.map((r) => r.id));
      let changed = false;
      const next = new Set();
      for (const rid of prev) {
        if (valid.has(rid)) next.add(rid);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [regs]);

  useLayoutEffect(() => {
    measureScrollWidth();
  }, [measureScrollWidth, filteredRegs, hiddenColumns, canSeeLeadOwner, canManage]);

  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureScrollWidth());
    ro.observe(el);
    if (bodyTableRef.current) ro.observe(bodyTableRef.current);
    return () => ro.disconnect();
  }, [measureScrollWidth, filteredRegs.length]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, rows] = await Promise.all([
        api.pmEvents.getById(id),
        api.pmEventRegistrations.getByEvent(id),
      ]);
      setEvent(ev);
      setRegs(rows ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load registrations', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCheckIn = async (reg) => {
    if (!canManage) return;
    try {
      const updated = await api.pmEventRegistrations.checkIn(reg.id, !reg.checked_in_at);
      setRegs((list) => list.map((r) => (r.id === reg.id ? { ...r, ...updated } : r)));
    } catch (err) {
      addToast({ type: 'error', title: 'Check-in failed', message: err.message });
    }
  };

  const handleConfirmationStatus = async (reg, nextStatus) => {
    if (!canManage) return;
    const prev = reg.confirmation_status ?? null;
    if (prev === nextStatus) return;
    setRegs((list) =>
      list.map((r) => (r.id === reg.id ? { ...r, confirmation_status: nextStatus } : r)),
    );
    try {
      const updated = await api.pmEventRegistrations.setConfirmationStatus(reg.id, nextStatus);
      setRegs((list) => list.map((r) => (r.id === reg.id ? { ...r, ...updated } : r)));
    } catch (err) {
      setRegs((list) =>
        list.map((r) => (r.id === reg.id ? { ...r, confirmation_status: prev } : r)),
      );
      addToast({
        type: 'error',
        title: 'Could not update confirmation status',
        message: err?.message || 'Permission denied',
      });
    }
  };

  const handleLinkLead = async (lead) => {
    if (!linkReg || !canManage) return;
    setLinking(true);
    try {
      const updated = await api.pmEventRegistrations.linkToLead(linkReg.id, lead.id);
      setRegs((list) => list.map((r) => (r.id === linkReg.id ? { ...r, ...updated } : r)));
      addToast({ type: 'success', title: 'Lead linked' });
      setLinkReg(null);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not link lead',
        message: err?.message || 'Permission denied or lead could not be linked',
      });
    } finally {
      setLinking(false);
    }
  };

  const handleCreateLead = async (reg) => {
    if (!canManage || !reg?.id) return;
    setCreatingLeadId(reg.id);
    try {
      const lead = await api.pmEventRegistrations.createLeadForRegistration(reg.id);
      setRegs((list) =>
        list.map((r) =>
          r.id === reg.id
            ? {
                ...r,
                lead_id: lead.id,
                lead: {
                  id: lead.id,
                  status: lead.status,
                  first_name: lead.first_name,
                  last_name: lead.last_name,
                  email: lead.email,
                  phone: lead.phone,
                  created_by: lead.created_by ?? user?.id ?? null,
                  exclusivity_status: lead.exclusivity_status ?? null,
                  owner: lead.created_by
                    ? { id: lead.created_by, full_name: resolveDisplayName(lead.created_by, user) }
                    : user
                      ? { id: user.id, full_name: user.full_name || null }
                      : null,
                },
              }
            : r,
        ),
      );
      addToast({ type: 'success', title: 'Lead created and linked' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not create lead',
        message: err?.message || 'Permission denied or lead could not be created',
      });
    } finally {
      setCreatingLeadId(null);
    }
  };

  const handleReassignLeadOwner = async (newOwnerId) => {
    if (!canInstantReassign || !ownerReg?.lead_id || !newOwnerId) return;
    const lead = ownerReg.lead;
    const oldOwnerId = lead?.created_by ?? null;
    if (newOwnerId === oldOwnerId) return;

    setReassigning(true);
    try {
      const updates = { created_by: newOwnerId };
      if (lead?.exclusivity_status === 'exclusive') {
        updates.exclusive_to = newOwnerId;
      }

      await api.leads.update(ownerReg.lead_id, updates);

      const oldOwnerName =
        resolveDisplayName(oldOwnerId, lead?.owner) || 'Unassigned';
      const newOwnerName = resolveDisplayName(newOwnerId) || 'Unknown';
      const actorName =
        resolveDisplayName(user?.id, user) || user?.full_name || 'Unknown';

      try {
        await api.activityLogs.create(ownerReg.lead_id, {
          activity_type: 'manual_calls',
          activity_date: todayDateKey(),
          notes: `Lead owner changed from ${oldOwnerName} to ${newOwnerName} by ${actorName}`,
        });
      } catch (logErr) {
        console.error('[EventRegistrations] activity log failed', logErr);
      }

      setRegs((list) =>
        list.map((r) =>
          r.id === ownerReg.id
            ? {
                ...r,
                lead: {
                  ...(r.lead || {}),
                  id: ownerReg.lead_id,
                  created_by: newOwnerId,
                  exclusivity_status: lead?.exclusivity_status ?? r.lead?.exclusivity_status ?? null,
                  owner: {
                    id: newOwnerId,
                    full_name: newOwnerName === 'Unknown' ? null : newOwnerName,
                  },
                },
              }
            : r,
        ),
      );
      addToast({ type: 'success', title: 'Lead owner updated' });
      setOwnerReg(null);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not change lead owner',
        message: err?.message || 'Permission denied',
      });
    } finally {
      setReassigning(false);
    }
  };

  const handleRequestLeadOwnerChange = async ({ proposedOwnerId, notes }) => {
    if (!canRequestOwnerChange || !requestReg?.lead_id || !proposedOwnerId) return;
    const lead = requestReg.lead;
    const currentOwnerId = lead?.created_by;
    if (!currentOwnerId) {
      addToast({
        type: 'error',
        title: 'Cannot request owner change',
        message: 'This lead has no current owner to transfer from.',
      });
      return;
    }
    if (!user?.agency_id || !user?.id) return;

    setRequesting(true);
    try {
      const proposedName = resolveDisplayName(proposedOwnerId) || 'Unknown';
      const reason = String(notes || '').trim();
      const combinedNotes = [
        `Proposed new owner: ${proposedName} (${proposedOwnerId})`,
        reason || null,
      ].filter(Boolean).join('\n');

      await api.ownershipTransfers.request({
        leadId: requestReg.lead_id,
        agencyId: user.agency_id,
        currentOwnerId,
        notes: combinedNotes,
      });

      addToast({
        type: 'success',
        title: 'Owner change request submitted',
        message: 'Pending Admin or Super Admin approval — ownership has not changed yet.',
      });
      setRequestReg(null);
      window.dispatchEvent(new CustomEvent('transfer-pending-change'));
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not submit owner change request',
        message: err?.message || 'Permission denied',
      });
    } finally {
      setRequesting(false);
    }
  };

  const openLeadOwnerAction = (reg) => {
    if (!reg?.lead_id) return;
    if (canInstantReassign) {
      setOwnerReg(reg);
      return;
    }
    if (canRequestOwnerChange) {
      setRequestReg(reg);
    }
  };

  const toggleAssociateFlag = async (reg) => {
    if (!canManage || reg.matched_user_id) return;
    const next = !reg.manually_flagged_associate;
    setRegs((list) =>
      list.map((r) => (r.id === reg.id ? { ...r, manually_flagged_associate: next } : r)),
    );
    try {
      const updated = await api.pmEventRegistrations.setAssociateFlag(reg.id, next);
      setRegs((list) => list.map((r) => (r.id === reg.id ? { ...r, ...updated } : r)));
    } catch (err) {
      setRegs((list) =>
        list.map((r) => (r.id === reg.id ? { ...r, manually_flagged_associate: !next } : r)),
      );
      addToast({
        type: 'error',
        title: 'Could not update associate flag',
        message: err.message,
      });
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setMoreFiltersOpen(false);
  };

  const resetDoorForm = () => {
    setDoorForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      session_id: '',
      invitation_link_id: '',
      invite_search: '',
      check_in_now: true,
    });
  };

  const submitDoor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.pmEventRegistrations.createDoor({
        event_id: id,
        first_name: doorForm.first_name,
        last_name: doorForm.last_name,
        email: doorForm.email || null,
        phone: doorForm.phone || null,
        session_id: event?.registration_scope === 'all_sessions' ? null : (doorForm.session_id || null),
        invitation_link_id: doorForm.invitation_link_id || null,
        check_in_now: doorForm.check_in_now,
      });
      addToast({ type: 'success', title: 'Walk-in registered' });
      setDoorOpen(false);
      resetDoorForm();
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not register walk-in', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  /** Seeds the dialog once from the event's saved reminder template, else a generic one. */
  const openCustomEmail = async () => {
    setCustomEmailPreviewing(false);
    setCustomEmailOpen(true);
    if (customEmailPrefilled.current) return;
    customEmailPrefilled.current = true;

    let subject = DEFAULT_CUSTOM_EMAIL_SUBJECT;
    let htmlBody = DEFAULT_CUSTOM_EMAIL_BODY;
    try {
      const settings = await api.pmEventEmailSettings.getByEvent(id);
      subject = String(settings?.reminder_subject || '').trim() || subject;
      htmlBody = String(settings?.reminder_body || '').trim() || htmlBody;
    } catch (err) {
      console.error('[EventRegistrations] custom email prefill failed:', err);
    }
    setCustomEmailForm({ subject, html_body: htmlBody });
  };

  const customEmailPreview = useMemo(() => {
    const values = buildPreviewMergeValues(event, event?.sessions);
    return {
      subject: applyPmEmailPreviewMerges(customEmailForm.subject, values),
      html: applyPmEmailPreviewMerges(customEmailForm.html_body, values, { html: true }),
    };
  }, [event, customEmailForm.subject, customEmailForm.html_body]);

  const confirmSendCustomEmail = async () => {
    setSendingCustomEmail(true);
    try {
      const result = await api.pmEventRegistrations.sendCustomEmail(id, {
        subject: customEmailForm.subject,
        htmlBody: customEmailForm.html_body,
      });
      const sent = Number(result?.sent) || 0;
      const failed = Number(result?.failed) || 0;
      addToast({
        type: failed ? 'warning' : 'success',
        title: 'Custom email sent',
        message: [`${sent} sent`, failed ? `${failed} failed` : null]
          .filter(Boolean)
          .join(' · '),
      });
      setCustomEmailOpen(false);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not send custom email', message: err.message });
    } finally {
      setSendingCustomEmail(false);
    }
  };

  const confirmSendReminders = async () => {
    setSendingReminders(true);
    try {
      const result = await api.pmEventRegistrations.sendRemindersNow(id);
      const sent = Number(result?.sent) || 0;
      const skipped = Number(result?.skipped) || 0;
      const failed = Number(result?.failed) || 0;
      const reasons = result?.skipped_reasons || {};
      const reasonParts = Object.entries(reasons)
        .filter(([, count]) => Number(count) > 0)
        .map(([key, count]) => `${count} ${key.replace(/_/g, ' ')}`);

      if (result?.message) {
        addToast({ type: 'warning', title: 'Reminders not sent', message: result.message });
      } else {
        addToast({
          type: failed ? 'warning' : 'success',
          title: 'Reminder emails sent',
          message: [
            `${sent} sent`,
            skipped ? `${skipped} skipped` : null,
            failed ? `${failed} failed` : null,
            reasonParts.length ? `(${reasonParts.join(', ')})` : null,
          ].filter(Boolean).join(' · '),
        });
      }

      setReminderConfirmOpen(false);
      if (sent > 0) await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not send reminders', message: err.message });
    } finally {
      setSendingReminders(false);
    }
  };

  const openReinviteModal = async () => {
    if (!canManage || !selectedCount || !user?.agency_id) return;
    setReinviteOpen(true);
    setReinviteTargetId('');
    setReinviteLoading(true);
    setReinviteTargets([]);
    try {
      const all = await api.pmEvents.getAll(user.agency_id);
      const candidates = (all ?? []).filter(
        (ev) => ev.status === 'published' && ev.id !== id,
      );
      const detailed = await Promise.all(
        candidates.map(async (ev) => {
          try {
            const full = await api.pmEvents.getById(ev.id);
            return { ...ev, sessions: full?.sessions ?? [] };
          } catch {
            return { ...ev, sessions: [] };
          }
        }),
      );
      const upcoming = detailed
        .filter((ev) => eventHasUpcomingSession(ev.sessions))
        .sort((a, b) => soonestUpcomingMs(a.sessions) - soonestUpcomingMs(b.sessions));
      setReinviteTargets(upcoming);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load events', message: err.message });
      setReinviteOpen(false);
    } finally {
      setReinviteLoading(false);
    }
  };

  const confirmSendReinvite = async () => {
    if (!reinviteTargetId || !selectedCount) return;
    setSendingReinvite(true);
    try {
      const result = await api.pmEventRegistrations.sendReinvite(
        [...selectedIds],
        reinviteTargetId,
      );
      const sent = Number(result?.sent) || 0;
      const skipped = Number(result?.skipped) || 0;
      const failed = Number(result?.failed) || 0;
      const reasons = result?.skipped_reasons || {};
      const reasonParts = Object.entries(reasons)
        .filter(([, count]) => Number(count) > 0)
        .map(([key, count]) => `${count} ${key.replace(/_/g, ' ')}`);

      addToast({
        type: failed ? 'warning' : 'success',
        title: 'Invites sent',
        message: [
          `${sent} sent`,
          skipped ? `${skipped} skipped` : null,
          failed ? `${failed} failed` : null,
          reasonParts.length ? `(${reasonParts.join(', ')})` : null,
        ].filter(Boolean).join(' · '),
      });
      setReinviteOpen(false);
      setSelectedIds(new Set());
    } catch (err) {
      addToast({ type: 'error', title: 'Could not send invites', message: err.message });
    } finally {
      setSendingReinvite(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={event ? `/mentors/${event.id}` : '/mentors'} className="text-gray-500 hover:text-gray-800">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Registrations</h1>
            <p className="text-sm text-gray-500">{event?.title}</p>
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              disabled={sendingReminders || !registrantsWithEmail.length}
              isLoading={sendingReminders}
              onClick={() => setReminderConfirmOpen(true)}
            >
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
              Send Reminder Now
            </Button>
            <Button
              variant="secondary"
              disabled={!registrantsWithEmail.length}
              onClick={() => void openCustomEmail()}
            >
              <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
              Send Custom Email
            </Button>
            <Button
              onClick={() => {
                resetDoorForm();
                setDoorOpen(true);
              }}
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Door / walk-in
            </Button>
          </div>
        )}
      </div>

      {!regs.length ? (
        <EmptyState
          title="No registrations yet"
          description="Public registrations and door walk-ins will show up here."
        />
      ) : (
        <>
          <Card className="p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-0.5 flex-1 min-w-[12rem]">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Search</span>
                <Input
                  placeholder="Name, email, or phone…"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  className="h-8 text-xs"
                />
              </label>
              <FilterSelect
                label="Check-in"
                value={filters.checkIn}
                onChange={(v) => setFilters((f) => ({ ...f, checkIn: v }))}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'checked_in', label: 'Checked in' },
                  { value: 'not_checked_in', label: 'Not checked in' },
                ]}
              />
              <FilterSelect
                label="Source"
                value={filters.source}
                onChange={(v) => setFilters((f) => ({ ...f, source: v }))}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'self', label: 'Self-registered' },
                  { value: 'door', label: 'Door walk-in' },
                ]}
              />
              {(event?.sessions?.length ?? 0) > 1 && event?.registration_scope !== 'all_sessions' && (
                <FilterSelect
                  label="Session"
                  value={filters.session}
                  onChange={(v) => setFilters((f) => ({ ...f, session: v }))}
                  options={sessionFilterOptions}
                  className="min-w-[10rem]"
                />
              )}
              <FilterSelect
                label="Associate"
                value={filters.associate}
                onChange={(v) => setFilters((f) => ({ ...f, associate: v }))}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'associate', label: 'Current Associates' },
                  { value: 'non_associate', label: 'Non-Associates' },
                ]}
              />
              <FilterSelect
                label="Confirmation Status"
                value={filters.rsvpStatus}
                onChange={(v) => setFilters((f) => ({ ...f, rsvpStatus: v }))}
                options={RSVP_STATUS_FILTER_OPTIONS}
              />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setMoreFiltersOpen((open) => !open)}
              >
                More filters
                {moreFiltersActiveCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-primary text-white text-[10px] font-semibold">
                    {moreFiltersActiveCount}
                  </span>
                )}
                <SafeIcon icon={moreFiltersOpen ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5" />
              </button>
              <div className="relative" ref={columnsMenuRef}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  onClick={() => setColumnsMenuOpen((open) => !open)}
                  aria-expanded={columnsMenuOpen}
                  aria-haspopup="menu"
                >
                  <SafeIcon icon={FiColumns} className="w-3.5 h-3.5" />
                  Columns
                  {hiddenColumns.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-primary text-white text-[10px] font-semibold">
                      {hiddenColumns.filter((k) =>
                        toggleableColumns.some((c) => c.key === k),
                      ).length}
                    </span>
                  )}
                  <SafeIcon icon={columnsMenuOpen ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5" />
                </button>
                {columnsMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 z-40 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                  >
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Show columns
                    </p>
                    {toggleableColumns.map((col) => {
                      const checked = isColVisible(col.key);
                      return (
                        <label
                          key={col.key}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            checked={checked}
                            onChange={() => handleToggleColumn(col.key)}
                          />
                          {col.label}
                        </label>
                      );
                    })}
                    <p className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100 mt-1">
                      Name stays pinned and always visible.
                    </p>
                  </div>
                )}
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="h-8 px-3 text-xs font-medium text-primary hover:underline"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>

            {moreFiltersOpen && (
              <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-gray-100">
                <FilterSelect
                  label="Registration type"
                  value={filters.regType}
                  onChange={(v) => setFilters((f) => ({ ...f, regType: v }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'primary', label: 'Primary' },
                    { value: 'guest', label: 'Guest' },
                  ]}
                />
                <FilterSelect
                  label="Confirmation email"
                  value={filters.confirmationStatus}
                  onChange={(v) => setFilters((f) => ({ ...f, confirmationStatus: v }))}
                  options={EMAIL_STATUS_FILTER_OPTIONS}
                />
                <FilterSelect
                  label="Reminder email"
                  value={filters.reminderStatus}
                  onChange={(v) => setFilters((f) => ({ ...f, reminderStatus: v }))}
                  options={EMAIL_STATUS_FILTER_OPTIONS}
                />
                <FilterSelect
                  label="Lead linked"
                  value={filters.leadLinked}
                  onChange={(v) => setFilters((f) => ({ ...f, leadLinked: v }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'has_lead', label: 'Has lead' },
                    { value: 'no_lead', label: 'No lead' },
                  ]}
                />
                <FilterSelect
                  label="Invited via link"
                  value={filters.invitedVia}
                  onChange={(v) => setFilters((f) => ({ ...f, invitedVia: v }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'has_invite', label: 'Has invitation link' },
                    { value: 'direct_or_door', label: 'Direct or door' },
                  ]}
                />
                <FilterSelect
                  label="Invite slug"
                  value={filters.inviteSlug}
                  onChange={(v) => setFilters((f) => ({ ...f, inviteSlug: v }))}
                  options={inviteSlugFilterOptions}
                  className="min-w-[10rem]"
                />
                <FilterSelect
                  label="Referred by"
                  value={filters.referredBy}
                  onChange={(v) => setFilters((f) => ({ ...f, referredBy: v }))}
                  options={referredByFilterOptions}
                  className="min-w-[10rem]"
                />
                {canSeeLeadOwner && (
                  <FilterSelect
                    label="Lead owner"
                    value={filters.leadOwner}
                    onChange={(v) => setFilters((f) => ({ ...f, leadOwner: v }))}
                    options={leadOwnerFilterOptions}
                    className="min-w-[10rem]"
                  />
                )}
              </div>
            )}

            <p className="text-xs text-gray-500">
              Showing {filteredRegs.length} of {regs.length} registration{regs.length === 1 ? '' : 's'}
            </p>
          </Card>

          {canManage && selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
              <span className="text-sm font-medium text-gray-800">
                {selectedCount} selected
              </span>
              <Button
                type="button"
                variant="secondary"
                disabled={sendingReinvite}
                onClick={() => void openReinviteModal()}
              >
                <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
                Invite to another event
              </Button>
              <button
                type="button"
                className="text-xs font-medium text-gray-500 hover:text-gray-800 underline"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </button>
            </div>
          )}

          {!filteredRegs.length ? (
            <EmptyState
              title="No matching registrations"
              description="Try adjusting or clearing your filters."
            />
          ) : (
            <Card className="overflow-hidden p-0">
              {/* Sticky top scrollbar + header (outside overflow-x so vertical sticky works). */}
              <div className="sticky top-0 z-20">
                <div
                  ref={topScrollRef}
                  className="overflow-x-auto bg-gray-100 border-b border-gray-200"
                  style={{ height: 10 }}
                  onScroll={(e) => syncScrollLeft(topScrollRef, e.currentTarget.scrollLeft)}
                  aria-hidden
                >
                  <div style={{ width: Math.max(scrollContentWidth, 1), height: 1 }} />
                </div>
                <div
                  ref={headerScrollRef}
                  className={`overflow-x-auto bg-gray-50 border-b border-gray-200 ${H_SCROLLBAR_HIDE}`}
                  onScroll={(e) => syncScrollLeft(headerScrollRef, e.currentTarget.scrollLeft)}
                >
                  <table
                    className="min-w-full text-sm"
                    style={scrollContentWidth ? { width: scrollContentWidth } : undefined}
                  >
                    <thead className="bg-gray-50 text-left text-gray-500">
                      <tr>
                        {canManage && (
                          <th className={CHECK_COL_CLASS}>
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={allPageSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = somePageSelected;
                              }}
                              onChange={toggleSelectAllOnPage}
                              title="Select all on page"
                              aria-label="Select all on page"
                            />
                          </th>
                        )}
                        <th className={`px-4 py-3 font-medium bg-gray-50 ${nameColClass} z-[2]`}>
                          Name
                        </th>
                        {isColVisible('contact') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Contact</th>
                        )}
                        {isColVisible('source') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Source</th>
                        )}
                        <th className="px-4 py-3 font-medium whitespace-nowrap">Session</th>
                        {isColVisible('invite') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Invite</th>
                        )}
                        {isColVisible('referred_by') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Referred by</th>
                        )}
                        {canSeeLeadOwner && isColVisible('lead_owner') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Lead Owner</th>
                        )}
                        {isColVisible('registered') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Registered</th>
                        )}
                        {isColVisible('confirmation_status') && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Confirmation Status</th>
                        )}
                        {showPaymentColumn && (
                          <th className="px-4 py-3 font-medium whitespace-nowrap">Payment</th>
                        )}
                        <th className="px-4 py-3 font-medium whitespace-nowrap">Confirmation</th>
                        <th className="px-4 py-3 font-medium whitespace-nowrap">Reminder</th>
                        <th className="px-4 py-3 font-medium whitespace-nowrap">Check-in</th>
                      </tr>
                    </thead>
                  </table>
                </div>
              </div>

              <div
                ref={bodyScrollRef}
                className="overflow-x-auto"
                onScroll={(e) => syncScrollLeft(bodyScrollRef, e.currentTarget.scrollLeft)}
              >
                <table
                  ref={bodyTableRef}
                  className="min-w-full text-sm"
                  style={scrollContentWidth ? { width: scrollContentWidth } : undefined}
                >
                  <tbody className="divide-y divide-gray-100">
                    {filteredRegs.map((reg) => {
                      const name = registrantDisplayName(reg);
                      const isCreating = creatingLeadId === reg.id;
                      const associate = isAssociateReg(reg);
                      const sessionRow = (event?.sessions || []).find((s) => s.id === reg.session_id);
                      const sessionLabel = event?.registration_scope === 'all_sessions'
                        ? 'All sessions'
                        : (sessionRow ? sessionOptionLabel(sessionRow) : '—');
                      return (
                        <tr key={reg.id} className="group hover:bg-gray-50/80">
                          {canManage && (
                            <td className={CHECK_CELL_CLASS}>
                              <input
                                type="checkbox"
                                className="rounded border-gray-300"
                                checked={selectedIds.has(reg.id)}
                                onChange={() => toggleSelectOne(reg.id)}
                                aria-label={`Select ${name}`}
                              />
                            </td>
                          )}
                          <td className={`px-4 py-3 font-medium text-gray-900 bg-white group-hover:bg-gray-50 ${nameColClass}`}>
                            {reg.lead_id ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <a
                                  href={`/leads/${reg.lead_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline font-medium"
                                >
                                  {name}
                                </a>
                                {associate && (
                                  <Badge variant="success" className="bg-purple-100 text-purple-800">
                                    Associate
                                  </Badge>
                                )}
                                {reg.is_guest_of && (
                                  <Badge variant="default">Guest</Badge>
                                )}
                              </div>
                            ) : (
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span>{name}</span>
                                  {associate && (
                                    <Badge variant="success" className="bg-purple-100 text-purple-800">
                                      Associate
                                    </Badge>
                                  )}
                                  {reg.is_guest_of && (
                                    <Badge variant="default">Guest</Badge>
                                  )}
                                </div>
                                {canManage && (
                                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-primary hover:underline"
                                      onClick={() => setLinkReg(reg)}
                                      disabled={isCreating}
                                    >
                                      Link to Lead
                                    </button>
                                    <span className="text-xs text-gray-300">·</span>
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                                      onClick={() => void handleCreateLead(reg)}
                                      disabled={isCreating}
                                    >
                                      {isCreating ? 'Creating…' : 'Create Lead'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            {canManage && !reg.matched_user_id && (
                              <button
                                type="button"
                                className="mt-1.5 block text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline"
                                onClick={() => void toggleAssociateFlag(reg)}
                              >
                                {reg.manually_flagged_associate ? 'Unflag' : 'Flag as Associate'}
                              </button>
                            )}
                          </td>
                          {isColVisible('contact') && (
                            <td className="px-4 py-3 text-gray-600">
                              <div>{reg.email || '—'}</div>
                              <div className="text-xs">{reg.phone || ''}</div>
                            </td>
                          )}
                          {isColVisible('source') && (
                            <td className="px-4 py-3">
                              <Badge variant={reg.source === 'door' ? 'warning' : 'primary'}>{reg.source}</Badge>
                            </td>
                          )}
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{sessionLabel}</td>
                          {isColVisible('invite') && (
                            <td className="px-4 py-3 text-gray-600">
                              {reg.invitation_link?.slug
                                ? `/${reg.invitation_link.slug}`
                                : '—'}
                            </td>
                          )}
                          {isColVisible('referred_by') && (
                            <td className="px-4 py-3 text-gray-600 max-w-[12rem]">
                              {reg.referred_by_text
                                ? (
                                  <span className="block truncate" title={reg.referred_by_text}>
                                    {reg.referred_by_text}
                                  </span>
                                )
                                : '—'}
                            </td>
                          )}
                          {canSeeLeadOwner && isColVisible('lead_owner') && (
                            <td className="px-4 py-3 text-gray-600">
                              {!reg.lead_id ? (
                                <span className="text-gray-400">—</span>
                              ) : (canInstantReassign || canRequestOwnerChange) ? (
                                <button
                                  type="button"
                                  className="text-primary hover:underline font-medium text-left"
                                  onClick={() => openLeadOwnerAction(reg)}
                                >
                                  {leadOwnerLabel(reg.lead) || 'Unassigned'}
                                </button>
                              ) : (
                                <span>{leadOwnerLabel(reg.lead) || 'Unassigned'}</span>
                              )}
                            </td>
                          )}
                          {isColVisible('registered') && (
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(reg.created_at)}</td>
                          )}
                          {isColVisible('confirmation_status') && (
                            <td className="px-4 py-3">
                              {canManage ? (
                                <select
                                  value={reg.confirmation_status || ''}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    void handleConfirmationStatus(reg, raw === '' ? null : raw);
                                  }}
                                  className={`h-8 min-w-[8.5rem] px-2 text-xs font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-primary ${rsvpStatusClassName(reg.confirmation_status)}`}
                                  aria-label={`Confirmation status for ${name}`}
                                >
                                  <option value="">—</option>
                                  {RSVP_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium border ${rsvpStatusClassName(reg.confirmation_status)}`}
                                >
                                  {rsvpStatusLabel(reg.confirmation_status)}
                                </span>
                              )}
                            </td>
                          )}
                          {showPaymentColumn && (
                            <td className="px-4 py-3">
                              <PaymentStatusBadge
                                status={reg.payment_status}
                                tier={reg.payment_tier}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <EmailStatusBadge status={reg.confirmation_email_status} />
                          </td>
                          <td className="px-4 py-3">
                            <EmailStatusBadge status={reg.reminder_email_status} />
                          </td>
                          <td className="px-4 py-3">
                            {canManage ? (
                              <button
                                type="button"
                                onClick={() => toggleCheckIn(reg)}
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${
                                  reg.checked_in_at
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                                {reg.checked_in_at ? 'Checked in' : 'Check in'}
                              </button>
                            ) : (
                              <span className="text-gray-500">
                                {reg.checked_in_at ? formatDate(reg.checked_in_at) : '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      <LinkLeadModal
        isOpen={!!linkReg}
        onClose={() => !linking && setLinkReg(null)}
        agencyId={user?.agency_id}
        onSelect={(lead) => void handleLinkLead(lead)}
        linking={linking}
      />

      <ChangeLeadOwnerModal
        isOpen={!!ownerReg}
        onClose={() => !reassigning && setOwnerReg(null)}
        reg={ownerReg}
        onSaved={(newOwnerId) => void handleReassignLeadOwner(newOwnerId)}
        saving={reassigning}
      />

      <RequestLeadOwnerChangeModal
        isOpen={!!requestReg}
        onClose={() => !requesting && setRequestReg(null)}
        reg={requestReg}
        onSubmit={(payload) => void handleRequestLeadOwnerChange(payload)}
        saving={requesting}
      />

      <Modal
        isOpen={customEmailOpen}
        onClose={() => !sendingCustomEmail && setCustomEmailOpen(false)}
        title="Send custom email"
        className="max-w-3xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Sends to every current registrant with an email on file
            {registrantsWithEmail.length
              ? ` (${registrantsWithEmail.length} recipient${registrantsWithEmail.length === 1 ? '' : 's'})`
              : ''}
            . Merge fields like {'{{first_name}}'} and {'{{session_date}}'} are filled in per recipient.
          </p>

          <Input
            label="Subject"
            value={customEmailForm.subject}
            onChange={(e) =>
              setCustomEmailForm((f) => ({ ...f, subject: e.target.value }))
            }
            disabled={sendingCustomEmail}
          />

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Body (HTML)</span>
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  className={`h-7 px-3 text-xs font-medium ${
                    customEmailPreviewing
                      ? 'bg-white text-gray-600 hover:bg-gray-50'
                      : 'bg-primary text-white'
                  }`}
                  onClick={() => setCustomEmailPreviewing(false)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={`h-7 px-3 text-xs font-medium border-l border-gray-300 ${
                    customEmailPreviewing
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setCustomEmailPreviewing(true)}
                >
                  Preview
                </button>
              </div>
            </div>

            {customEmailPreviewing ? (
              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                <div className="px-3 py-2 text-sm font-medium text-gray-900 border-b border-gray-200 bg-white">
                  {customEmailPreview.subject || '(no subject)'}
                </div>
                <iframe
                  srcDoc={
                    customEmailPreview.html
                    || '<p style="padding:20px;color:#999;font-family:sans-serif;">No HTML content yet.</p>'
                  }
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: '420px', border: 'none' }}
                  title="Custom email preview"
                />
              </div>
            ) : (
              <textarea
                value={customEmailForm.html_body}
                onChange={(e) =>
                  setCustomEmailForm((f) => ({ ...f, html_body: e.target.value }))
                }
                disabled={sendingCustomEmail}
                rows={14}
                spellCheck={false}
                placeholder="Paste your HTML here…"
                className="w-full rounded-lg border border-lineGray px-3 py-2 font-mono text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
              />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={sendingCustomEmail}
              onClick={() => setCustomEmailOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={sendingCustomEmail}
              disabled={
                sendingCustomEmail
                || !customEmailForm.subject.trim()
                || !customEmailForm.html_body.trim()
              }
              onClick={() => void confirmSendCustomEmail()}
            >
              Send email
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={reinviteOpen}
        onClose={() => !sendingReinvite && setReinviteOpen(false)}
        title="Invite to another event"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Send an invitation email to{' '}
            <strong>
              {selectedCount} contact{selectedCount === 1 ? '' : 's'}
            </strong>{' '}
            for a different published event. Each invite is attributed to the original
            inviter (or the event agent / lead owner when no invite link exists).
          </p>

          {reinviteLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : !reinviteTargets.length ? (
            <EmptyState
              title="No upcoming published events"
              description="Create or publish an event with a future session to invite these contacts."
            />
          ) : (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">Target event</span>
              <select
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={reinviteTargetId}
                onChange={(e) => setReinviteTargetId(e.target.value)}
                disabled={sendingReinvite}
              >
                <option value="">Select an event…</option>
                {reinviteTargets.map((ev) => {
                  const nextSession = soonestUpcomingSession(ev.sessions);
                  const when = nextSession ? formatSessionOptionLabel(nextSession) : '';
                  return (
                    <option key={ev.id} value={ev.id}>
                      {ev.title}
                      {when ? ` — ${when}` : ''}
                    </option>
                  );
                })}
              </select>
            </label>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={sendingReinvite}
              onClick={() => setReinviteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={sendingReinvite}
              disabled={
                sendingReinvite
                || reinviteLoading
                || !reinviteTargetId
                || !selectedCount
              }
              onClick={() => void confirmSendReinvite()}
            >
              Send invites ({selectedCount})
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={reminderConfirmOpen}
        onClose={() => !sendingReminders && setReminderConfirmOpen(false)}
        title="Send reminder emails now?"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {event?.registration_scope === 'all_sessions'
              ? 'This will immediately email the reminder for the next upcoming session to every current registrant who has an email on file'
              : 'This will immediately email the event reminder to every current registrant who has an email on file'}
            {registrantsWithEmail.length
              ? ` (${registrantsWithEmail.length} recipient${registrantsWithEmail.length === 1 ? '' : 's'})`
              : ''}
            . Scheduled automatic reminders for those registrants will be skipped afterward.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={sendingReminders}
              onClick={() => setReminderConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={sendingReminders}
              disabled={sendingReminders || !registrantsWithEmail.length}
              onClick={confirmSendReminders}
            >
              Send reminders
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={doorOpen} onClose={() => setDoorOpen(false)} title="Door / walk-in registration">
        <form onSubmit={submitDoor} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First name"
              value={doorForm.first_name}
              onChange={(e) => setDoorForm((f) => ({ ...f, first_name: e.target.value }))}
              required
            />
            <Input
              label="Last name"
              value={doorForm.last_name}
              onChange={(e) => setDoorForm((f) => ({ ...f, last_name: e.target.value }))}
              required
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={doorForm.email}
            onChange={(e) => setDoorForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Phone"
            value={doorForm.phone}
            onChange={(e) => setDoorForm((f) => ({ ...f, phone: e.target.value }))}
          />
          {event?.registration_scope === 'all_sessions' ? (
            <p className="text-sm text-gray-500">This sign-up covers every session.</p>
          ) : (event?.sessions?.length > 0) && (
            <label className="block text-sm">
              <span className="font-medium text-gray-700 mb-1 block">Session</span>
              <select
                className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                value={doorForm.session_id}
                onChange={(e) => setDoorForm((f) => ({ ...f, session_id: e.target.value }))}
              >
                <option value="">Any / none</option>
                {event.sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {sessionOptionLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(event?.invitation_links?.length > 0) && (
            <div className="space-y-2">
              <Input
                label="Invited via (optional)"
                placeholder="Search invite links…"
                value={doorForm.invite_search}
                onChange={(e) => setDoorForm((f) => ({ ...f, invite_search: e.target.value }))}
              />
              <label className="block text-sm">
                <span className="font-medium text-gray-700 mb-1 block">Select invite link</span>
                <select
                  className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                  value={doorForm.invitation_link_id}
                  onChange={(e) => setDoorForm((f) => ({ ...f, invitation_link_id: e.target.value }))}
                >
                  <option value="">None / direct walk-in</option>
                  {inviteOptions.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inviteOptionLabel(inv)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={doorForm.check_in_now}
              onChange={(e) => setDoorForm((f) => ({ ...f, check_in_now: e.target.checked }))}
            />
            Check in now
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setDoorOpen(false)}>Cancel</Button>
            <Button type="submit" isLoading={saving}>Register</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
